import { Set, List, Map } from 'immutable';
import _isString from 'lodash/isString';
import _isNumber from 'lodash/isNumber';
function makeCondition(condition) {
  return condition.update('dependents', Set).update('options', Set);
}
function makeQuestions(questions) {
  if (questions) {
    return questions.map(function (value) {
      return List.isList(value) ? Set(value) : makeQuestions(value);
    });
  }
  return null;
}
function makeConditions(conditions) {
  return conditions.map(makeCondition);
}
function immutablePairs(immutableMap) {
  // Map.entrySeq() is dumb and creates (mutable) Array entries.
  // Undumb it.
  return immutableMap.entrySeq().map(List);
}
function immutableIndexBy(immutableListOfMaps, key) {
  return Map(immutableListOfMaps.map(function (t) {
    return t.get(key);
  }).zip(immutableListOfMaps));
}
function reduceToSet(iter) {
  return iter.reduce(function (accumulated, items) {
    return accumulated.union(items);
  }, Set());
}
function createAnswerSet(answers, prefix) {
  var outputPrefix = prefix ? prefix + '__' : '';
  return reduceToSet(immutablePairs(answers).map(function (entry) {
    // Multi-composite or multi-select
    if (List.isList(entry.last())) {
      // Multi-composite
      if (entry.last().every(Map.isMap)) {
        return reduceToSet(entry.last().map(function (item) {
          return createAnswerSet(item, '' + outputPrefix + entry.first());
        }));
      }
      // Multi-select
      return entry.last().map(function (value) {
        return List.of('' + outputPrefix + entry.first(), value);
      });
    }
    // Simple select case
    return List.of(List.of('' + outputPrefix + entry.first(), entry.last()));
  })).union(answers.keySeq().map(function (answer) {
    return '' + outputPrefix + answer;
  }));
}
function rebuild(questions, metas) {
  var doneState = questions.get('__done__');
  return questions
  // Filters out '__done__' and questions without defined meta
  .filter(function (value, globalSlug) {
    return metas.has(globalSlug);
  }).map(function (value, globalSlug) {
    // Test for multicomposite
    var meta = metas.get(globalSlug);
    var isDone = doneState.has(globalSlug);
    if (meta.get('nested')) {
      var composites = void 0;
      if (value === null) {
        composites = List();
        isDone = false;
      } else {
        composites = value.map(function (composite) {
          return rebuild(composite, metas);
        });
        isDone = composites.every(function (composite) {
          return composite.every(function (subquestion) {
            return subquestion.get('done');
          });
        });
      }
      return meta.set('done', isDone).set('composites', composites);
    }
    var choices = meta.get('choices');
    if (choices) {
      choices = choices.filter(function (choice) {
        return value.has(choice);
      });
    }
    return meta.set('done', isDone).set('choices', choices);
  }).valueSeq().sortBy(function (q) {
    return q.get('index');
  }).toList();
}
var _slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;
    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);
        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();
var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) {
  return typeof obj;
} : function (obj) {
  return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
};
function wordToInteger(word) {
  return {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5
  }[word];
}
function singleConditionSatisfied(condition, answered) {
  if (condition.get('type') === 'All') {
    return condition.get('options').isSubset(answered);
  } else if (condition.get('type') === 'Any') {
    return condition.get('options').intersect(answered).size > 0;
  }
  return false;
}
function singleConditionPrerequisites(condition, conditions) {
  var options = condition.get('options');
  var extendedOptions = options.union(options.filter(List.isList).map(function (opt) {
    return opt.first();
  }));
  return conditions.filter(function (cond) {
    return cond.get('dependents').intersect(extendedOptions).size > 0;
  });
}
function prerequisites(condition, conditions, cumulation) {
  var initialValue = (cumulation || List()).push(condition);
  return singleConditionPrerequisites(condition, conditions).reduce(function (accumulator, parentCondition) {
    return prerequisites(parentCondition, conditions, accumulator);
  }, initialValue);
}
function prerequisitesSatisfied(prereqs, answered) {
  return prereqs.every(function (check) {
    return singleConditionSatisfied(check, answered);
  });
}
function unitFirstFloorSatisfied(condition, answered) {
  var unitFirstFloorToCheck = condition.get('dependents').first();
  var answeredBuildingHeight = answered.find(function (item) {
    return (typeof item === 'undefined' ? 'undefined' : _typeof(item)) === 'object' && item.get(0) === 'building_height';
  });
  var answeredUnitHeight = answered.find(function (item) {
    return (typeof item === 'undefined' ? 'undefined' : _typeof(item)) === 'object' && item.get(0) === 'unit_height';
  });
  if (!answeredBuildingHeight) {
    return false;
  }
  if (answeredBuildingHeight.get(1) === 'six_or_more') {
    return false;
  }
  if (!answeredUnitHeight) {
    return false;
  }
  var buildingHeight = wordToInteger(answeredBuildingHeight.get(1));
  var unitHeight = wordToInteger(answeredUnitHeight.get(1));
  var unitFirstFloor = wordToInteger(unitFirstFloorToCheck.get(1));
  return buildingHeight - unitFirstFloor + 1 >= unitHeight;
}
function unitHeightSatisfied(condition, answered) {
  var unitHeightToCheck = condition.get('dependents').first();
  var answeredBuildingHeight = answered.find(function (item) {
    return (typeof item === 'undefined' ? 'undefined' : _typeof(item)) === 'object' && item.get(0) === 'building_height';
  });
  var answeredUnitFirstFloor = answered.find(function (item) {
    return (typeof item === 'undefined' ? 'undefined' : _typeof(item)) === 'object' && item.get(0) === 'unit_first_floor';
  });
  if (!answeredBuildingHeight) {
    return false;
  }
  if (answeredBuildingHeight.get(1) === 'six_or_more') {
    return true;
  }
  if (!answeredUnitFirstFloor) {
    return false;
  }
  var buildingHeight = wordToInteger(answeredBuildingHeight.get(1));
  var unitFirstFloor = wordToInteger(answeredUnitFirstFloor.get(1));
  var unitHeight = wordToInteger(unitHeightToCheck.get(1));
  return buildingHeight - unitFirstFloor + 1 >= unitHeight;
}
function satisfied(condition, answered, conditions) {
  // This allows us to check each option as a single option, then compare each result
  var prerequisitesPerOption = condition.get('options').map(function (opt) {
    var singleOptionSet = Set().add(opt);
    var conditionWithSingleOption = condition.set('options', singleOptionSet);
    return prerequisites(conditionWithSingleOption, conditions);
  });
  if (condition.get('type') === 'All') {
    return prerequisitesPerOption.every(function (optionPrerequisites) {
      return prerequisitesSatisfied(optionPrerequisites, answered);
    });
  } else if (condition.get('type') === 'Any') {
    return prerequisitesPerOption.some(function (optionPrerequisites) {
      return prerequisitesSatisfied(optionPrerequisites, answered);
    });
  } else if (condition.get('type') === 'MaxUnitFirstFloor') {
    return unitFirstFloorSatisfied(condition, answered);
  } else if (condition.get('type') === 'MaxUnitHeight') {
    return unitHeightSatisfied(condition, answered);
  }
  return false;
}
function remove(questions, dependent) {
  if (_isString(dependent)) {
    return questions.delete(dependent);
  }
  if (questions.has(dependent.first())) {
    return questions.updateIn([dependent.first()], function (choices) {
      return choices.delete(dependent.get(1));
    });
  }
  return questions;
}
function buildParameters(questions, answers, inheritedAnswerSet, answerPrefix) {
  var answerSet = createAnswerSet(answers, answerPrefix);
  if (inheritedAnswerSet) {
    answerSet = inheritedAnswerSet.union(answerSet);
  }
  var expandedQuestions = questions.map(function (value, key) {
    // Check if this value is a multi composite
    if (Map.isMap(value)) {
      // Check if the user has answered anything for it
      if (answers.has(key)) {
        // Remove all answers for this multi-composite
        // The individual instances will re-append themselves
        var newPrefix = answerPrefix ? answerPrefix + '__' + key : key;
        var excludedAnswerSet = reduceToSet(answers.get(key).map(function (answerValue) {
          return createAnswerSet(answerValue, newPrefix);
        }));
        var scopedAnswerSet = answerSet.subtract(excludedAnswerSet);
        return answers.get(key).map(function (singleMultiCompositeAnswer) {
          return buildParameters(value, singleMultiCompositeAnswer, scopedAnswerSet, newPrefix);
        });
      }
      return null;
    }
    return value;
  });
  return List.of(answerSet, expandedQuestions);
}

/*
 * Given questions and an answered set, return a set of question slugs which are answered.
 *
 * This looks more complex than it should be because it also tries to verify the answer
 * enumerations are available.  Furthermore, it accepts empty set as an answer for enumerations.
 */
function findAnsweredQuestions(questions, answerSet) {
  return questions.entrySeq().filter(function (_ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      slug = _ref2[0],
      answers = _ref2[1];

    // Multi composites handled later in rebuild since we don't have enough info now
    if (!answers) {
      return false;
    }

    // `answers` typed Set are enumerations
    var questionAnswered = Set.isSet(answers) && answerSet.has(slug);

    // Filter out non-matching answer_set answers
    var relevantAnswerSet = answerSet.filter(function (answer) {
      return List.isList(answer) ? answer.first() === slug : false;
    }).map(function (answer) {
      // 'Accepts' answer if type 'NUMBER' and is a number
      if (answers.last() === 'NUMBER' && _isNumber(answer.last())) {
        // 'NUMBER' answer is represented as [question_slug, 'NUMBER']
        return List.of(slug, 'NUMBER');
        // 'Accepts' answer if type 'TEXT' and is string of more than one character
        // If the answer is length 0, the answer will keep it's current value of '',
        // which means it will be filtered out at the answers_valid stage below
      } else if (answers.last() === 'TEXT' && _isString(answer.last()) && answer.last().length > 0) {
        // 'TEXT' answer is represented as [question_slug, 'TEXT']
        return List.of(slug, 'TEXT');
      }
      // Represented as [question_slug, answer_slug]
      return answer;
    });
    var availableAnswers = answers.map(function (answer) {
      return List.of(slug, answer);
    }).toSet();

    // Filters out any answers that don't match [question slug, valid question answer slug] format
    // For number/text inputs, this is [question slug, 'NUMBER'/'TEXT']
    // We accept empty answers because that represents selecting "none" in a multiselect
    var answersValid = relevantAnswerSet.intersect(availableAnswers).size > 0 || relevantAnswerSet.size === 0;
    return questionAnswered && answersValid;
  }).map(function (_ref3) {
    var _ref4 = _slicedToArray(_ref3, 1),
      slug = _ref4[0];
    return slug;
  }).toSet();
}
function singleQuestionsTrimmer(params, conditions) {
  var answerSet = params.first();
  var questions = conditions.reduce(function (questionsAccumulator, condition) {
    if (satisfied(condition, answerSet, conditions)) {
      return questionsAccumulator;
    }
    return condition.get('dependents').reduce(remove, questionsAccumulator);
  }, params.last()).filter(function (q) {
    return Set.isSet(q) ? q.size !== 0 : true;
  });
  return questions.set('__done__', findAnsweredQuestions(questions, answerSet));
}
function questionsTrimmer(answers, questions, conditions) {
  var parameters = buildParameters(questions, answers);
  var conditionedQuestionsTrimmer = function conditionedQuestionsTrimmer(params) {
    return singleQuestionsTrimmer(params, conditions);
  };
  return conditionedQuestionsTrimmer(parameters).map(function (value) {
    // If multicomposite, it's a List - the others are Set
    if (List.isList(value)) {
      return value.map(conditionedQuestionsTrimmer);
    }
    return value;
  });
}
var _createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }
  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
var Trimmer = function () {
  function Trimmer(path, meta) {
    _classCallCheck(this, Trimmer);
    this.questions = makeQuestions(path.get('questions'));
    this.conditions = makeConditions(path.get('conditions'));
    this.meta = immutableIndexBy(meta.map(function (m, i) {
      return m.set('index', i);
    }), 'global_slug');
  }
  _createClass(Trimmer, [{
    key: 'reconstructQuestions',
    value: function reconstructQuestions(answers) {
      var questions = questionsTrimmer(answers, this.questions, this.conditions);
      return rebuild(questions, this.meta);
    }
  }]);
  return Trimmer;
}();
export { Trimmer };